/**
 * /admin/apps — every app on the platform regardless of who can see it.
 * Click into a row to manage it (disable, enable, delete, self-grant).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApiError } from '../../api/client.js';
import { api } from '../../api/client.js';
import type { AdminAppRow } from '../../api/types.js';
import { AdminTabs } from '../../components/AdminTabs.js';
import { IconChevronRight, IconGlobe, IconLock, IconSearch } from '../../components/Icons.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { Banner, Mono, PageHeader, PageSkeleton, inputClass } from '../../components/ui.js';
import { relTime } from '../../lib/format.js';

interface State {
  loading: boolean;
  apps?: AdminAppRow[];
  error?: string;
}

export function AdminApps() {
  const [state, setState] = useState<State>({ loading: true });
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ apps: AdminAppRow[] }>('/api/admin/apps')
      .then((res) => {
        if (cancelled) return;
        setState({ loading: false, apps: res.apps });
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setState({ loading: false, error: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (!state.apps) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return state.apps;
    return state.apps.filter((a) =>
      [a.name, a.slug, a.description ?? ''].join(' ').toLowerCase().includes(q),
    );
  }, [state.apps, filter]);

  if (state.loading) return <PageSkeleton rows={6} />;

  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Admin"
        title="All apps"
        description="Every app on Kin, including private ones you're not part of. Click in to disable, delete, or self-grant access."
      />

      <AdminTabs />

      {state.error && <Banner tone="error">{state.error}</Banner>}

      {state.apps && state.apps.length > 0 && (
        <div className="relative mb-5">
          <IconSearch
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-faint pointer-events-none"
          />
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name or slug…"
            aria-label="Filter apps"
            className={`${inputClass} pl-9`}
          />
        </div>
      )}

      <ul className="divide-y divide-line rounded-xl border border-line bg-surface overflow-hidden">
        {filtered.map((app) => (
          <li key={app.id}>
            <Link
              to={`/admin/apps/${app.slug}`}
              className="group flex items-center justify-between gap-4 px-5 py-4 hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:bg-surface-2"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-semibold text-base text-ink truncate">
                    {app.name}
                  </span>
                  <StatusBadge status={app.status} />
                  <span
                    className={
                      'inline-flex items-center gap-1 text-xs ' +
                      (app.visibility === 'workspace' ? 'text-info' : 'text-muted')
                    }
                  >
                    {app.visibility === 'workspace' ? (
                      <>
                        <IconGlobe size={11} /> Mytra-wide
                      </>
                    ) : (
                      <>
                        <IconLock size={11} /> Private
                      </>
                    )}
                  </span>
                </div>
                <div className="text-muted text-sm mt-1 truncate">
                  <Mono>{app.slug}</Mono>
                  {app.description && <span className="ml-2 text-muted">{app.description}</span>}
                </div>
                <div className="text-xs text-faint mt-1">
                  {app.manager_count} manager{app.manager_count === 1 ? '' : 's'} ·{' '}
                  {app.role_grant_count} app-role grant{app.role_grant_count === 1 ? '' : 's'} ·
                  updated {relTime(app.updated_at)}
                </div>
              </div>
              <IconChevronRight
                size={16}
                className="text-faint group-hover:text-muted shrink-0 transition-colors"
              />
            </Link>
          </li>
        ))}
        {filtered.length === 0 && (
          <li className="px-5 py-8 text-center text-muted text-sm">
            {filter ? `No apps match "${filter}".` : 'No apps on Kin yet.'}
          </li>
        )}
      </ul>
    </div>
  );
}
